/**
 * fetch-with-network-retry
 *
 * Retries fetches that reject at the network layer (not HTTP errors) with
 * backoff, and reports each retry + final failure to analytics.
 *
 * These rejections surface as WebKit "Load failed" / Chromium "Failed to
 * fetch" TypeErrors and spike during zero-downtime deploys, when the app
 * container's network alias is swapped atomically and in-flight requests
 * briefly fail to resolve the host. A short retry rides over that window;
 * telemetry keeps the failures visible so deploy-related blips are tracked
 * rather than hidden.
 *
 * Primary responsibilities:
 * - Classify transient network rejections (vs. real errors / HTTP responses)
 * - Retry with capped exponential backoff, honoring AbortSignal
 * - Emit `network_fetch_retry` on each retry and `network_fetch_failed` when
 *   retries are exhausted, so Load failed spikes stay observable
 *
 * Dependencies:
 * - posthog-js (optional, browser only)
 */

import posthog from 'posthog-js'

const TRANSIENT_NETWORK_ERROR =
  /failed to fetch|load failed|network\s*error|networkerror when attempting to fetch/i

export const DEFAULT_NETWORK_RETRY_LIMIT = 3
const NETWORK_RETRY_BASE_DELAY_MS = 300
const NETWORK_RETRY_MAX_DELAY_MS = 2_000

// Circuit breaker: when the transport is down (deploy swap, offline laptop,
// dead WiFi), hundreds of components retry at once and flood both the
// network and analytics. After enough consecutive transport failures, fail
// fast for a cooldown so one outage can't lock the UI in a retry loop
// (danieldisu6 sessions: 2,548 console errors in 8s, Oct 2026).
const CIRCUIT_FAILURE_THRESHOLD = 8
const CIRCUIT_FAILURE_WINDOW_MS = 10_000
const CIRCUIT_OPEN_MS = 30_000
const CIRCUIT_PROBE_INTERVAL_MS = 5_000

// Telemetry budget: at most this many network events per window, no matter
// how many call sites fail. Excess failures are counted and attached to the
// next emitted event as `suppressed_since_last`, never sent one-by-one.
const TELEMETRY_BUDGET_PER_WINDOW = 10
const TELEMETRY_WINDOW_MS = 60_000

let consecutiveFailureTimes: number[] = []
let circuitOpenUntil = 0
let lastProbeAt = 0
let telemetryWindowStartedAt = 0
let telemetrySentInWindow = 0
let telemetrySuppressed = 0

export class NetworkCircuitOpenError extends TypeError {
  constructor() {
    super('Failed to fetch (network circuit open)')
    this.name = 'NetworkCircuitOpenError'
  }
}

function recordTransportFailure(now: number) {
  consecutiveFailureTimes = consecutiveFailureTimes.filter(
    startedAt => now - startedAt < CIRCUIT_FAILURE_WINDOW_MS
  )
  consecutiveFailureTimes.push(now)

  if (consecutiveFailureTimes.length >= CIRCUIT_FAILURE_THRESHOLD) {
    circuitOpenUntil = now + CIRCUIT_OPEN_MS
    consecutiveFailureTimes = []
  }
}

function recordTransportSuccess() {
  consecutiveFailureTimes = []
  circuitOpenUntil = 0
}

/** True when the breaker is open and this call is not the half-open probe. */
function isCircuitBlocking(now: number): boolean {
  if (now >= circuitOpenUntil) return false
  if (now - lastProbeAt >= CIRCUIT_PROBE_INTERVAL_MS) {
    // Half-open: let one probe through per interval to detect recovery.
    lastProbeAt = now
    return false
  }
  return true
}

function isOffline(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'onLine' in navigator &&
    navigator.onLine === false
  )
}

/** Test hook: reset module-level breaker/telemetry state between cases. */
export function resetNetworkRetryStateForTests() {
  consecutiveFailureTimes = []
  circuitOpenUntil = 0
  lastProbeAt = 0
  telemetryWindowStartedAt = 0
  telemetrySentInWindow = 0
  telemetrySuppressed = 0
}

export type NetworkRetryContext = {
  /** Logical name of the call site, e.g. 'authenticated-fetch'. */
  source?: string
}

export type FetchWithNetworkRetryOptions = {
  retryLimit?: number
  context?: NetworkRetryContext
}

// --------------

export function isTransientNetworkError(error: unknown): boolean {
  // A fetch network reject is a TypeError whose message matches the patterns
  // above. HTTP errors never throw -- they resolve a Response -- so this only
  // catches genuine transport failures.
  if (!(error instanceof TypeError)) {
    return false
  }

  return TRANSIENT_NETWORK_ERROR.test(error.message)
}

// --------------

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  )
}

// --------------

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

// --------------

function captureNetworkEvent(
  event: 'network_fetch_retry' | 'network_fetch_failed',
  properties: Record<string, unknown>
) {
  if (typeof window === 'undefined') return

  // Budget check runs before any capture: a failure burst (e.g. every
  // reaction bar on a page retrying during an outage) must not turn into
  // one analytics event per attempt -- that burst is what pushed
  // posthog-js into client rate limiting, whose per-drop console.error
  // became the thousands of replay console errors.
  const now = Date.now()
  if (now - telemetryWindowStartedAt >= TELEMETRY_WINDOW_MS) {
    telemetryWindowStartedAt = now
    telemetrySentInWindow = 0
  }

  if (telemetrySentInWindow >= TELEMETRY_BUDGET_PER_WINDOW) {
    telemetrySuppressed += 1
    return
  }

  telemetrySentInWindow += 1
  const suppressed = telemetrySuppressed
  telemetrySuppressed = 0

  try {
    ;(posthog as unknown as {
      capture?: (name: string, props?: Record<string, unknown>) => unknown
    }).capture?.(event, {
      ...properties,
      ...(suppressed > 0 ? { suppressed_since_last: suppressed } : {})
    })
  } catch {
    // Telemetry must never break the request path.
  }
}

// --------------

function backoffDelayMs(attempt: number): number {
  return Math.min(
    NETWORK_RETRY_MAX_DELAY_MS,
    NETWORK_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1)
  )
}

// --------------

function waitWithAbort(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'))
      return
    }

    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)

    const onAbort = () => {
      clearTimeout(timeout)
      reject(new DOMException('The operation was aborted.', 'AbortError'))
    }

    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

// --------------

/**
 * fetch wrapper that transparently retries transient network rejections.
 * HTTP responses (including 4xx/5xx) are returned unchanged on the first
 * try -- only genuine transport failures are retried. Non-transient errors
 * and aborts rethrow immediately.
 */
export async function fetchWithNetworkRetry(
  input: RequestInfo | URL,
  init: RequestInit = {},
  options: FetchWithNetworkRetryOptions = {}
): Promise<Response> {
  const retryLimit = options.retryLimit ?? DEFAULT_NETWORK_RETRY_LIMIT
  const signal = init.signal
  const url = getRequestUrl(input)
  const source = options.context?.source ?? 'unknown'

  let attempt = 0

  while (true) {
    attempt += 1

    // Fail fast while the transport is known-dead: no fetch, no retry
    // timers, no telemetry. One half-open probe per interval still checks
    // for recovery so the breaker closes promptly when the network returns.
    if (attempt === 1 && isCircuitBlocking(Date.now())) {
      throw new NetworkCircuitOpenError()
    }

    try {
      const response = await fetch(input, init)
      recordTransportSuccess()
      return response
    } catch (error) {
      // Never retry an abort or a non-network error -- surface it as-is.
      if (isAbortError(error) || !isTransientNetworkError(error)) {
        throw error
      }

      recordTransportFailure(Date.now())

      // Offline is not a blip to ride over: retrying only queues timers
      // and telemetry. Surface immediately; callers re-run on `online`.
      if (isOffline()) {
        throw error
      }

      const willRetry = attempt <= retryLimit && !isCircuitBlocking(Date.now())

      // One retry event per logical request (first retry only) plus one
      // failure event on exhaustion -- never one event per attempt.
      if (willRetry && attempt === 1) {
        captureNetworkEvent('network_fetch_retry', {
          source,
          url,
          attempt,
          retry_limit: retryLimit,
          message: error instanceof Error ? error.message : String(error)
        })
      }

      if (!willRetry) {
        captureNetworkEvent('network_fetch_failed', {
          source,
          url,
          attempt,
          retry_limit: retryLimit,
          message: error instanceof Error ? error.message : String(error)
        })
        // Keep the failure visible: rethrow so the caller's error path (and
        // error tracking) still sees it after retries are exhausted.
        throw error
      }

      await waitWithAbort(backoffDelayMs(attempt), signal)
    }
  }
}
